import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, usdFromMicros } from '../api.js';

export default function NewCampaign() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    intent: '',
    country: 'BR',
    state: 'SP',
    cities: 'São Paulo, Campinas',
    maxResults: 40,
    maxCostUsd: 0.5,
  });
  const [campaign, setCampaign] = useState(null);
  const [plan, setPlan] = useState(null);

  const create = useMutation({
    mutationFn: async () => {
      const input = {
        name: form.name || 'Campanha sem título',
        intent: form.intent,
        geography: {
          country: form.country,
          state: form.state,
          cities: form.cities
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        },
        maxResults: Number(form.maxResults) || 40,
        maxCostMicroUsd: Math.round(Number(form.maxCostUsd) * 1_000_000),
      };
      const inputKey = JSON.stringify(input);
      const created = campaign?.inputKey === inputKey ? campaign : await api.createCampaign(input);
      setCampaign({ ...created, inputKey });
      const p = await api.planCampaign(created.id);
      setPlan(p);
      setStep(3);
    },
  });

  const startM = useMutation({
    mutationFn: async () => {
      await api.startCampaign(campaign.id);
      nav(`/campaigns/${campaign.id}`);
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold">Nova campanha</h1>
      <Stepper step={step} />

      {step === 1 && (
        <Panel>
          <Field label="Nome da campanha">
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Materiais de construção SP"
            />
          </Field>
          <Field label="O que você procura?">
            <textarea
              className="input"
              rows={4}
              value={form.intent}
              onChange={(e) => setForm({ ...form, intent: e.target.value })}
              placeholder="Ex.: Depósitos de materiais de construção na Grande SP que vendam areia, pedra e cimento."
            />
          </Field>
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!form.intent}
              onClick={() => setStep(2)}
              className="btn-primary"
            >
              Continuar
            </button>
          </div>
        </Panel>
      )}

      {step === 2 && (
        <Panel>
          <div className="grid grid-cols-2 gap-4">
            <Field label="País">
              <input
                className="input"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              />
            </Field>
            <Field label="Estado">
              <input
                className="input"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Cidades (separadas por vírgula)">
            <input
              className="input"
              value={form.cities}
              onChange={(e) => setForm({ ...form, cities: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Máximo de resultados">
              <input
                type="number"
                className="input"
                value={form.maxResults}
                onChange={(e) => setForm({ ...form, maxResults: e.target.value })}
              />
            </Field>
            <Field label="Budget máximo (USD)">
              <input
                type="number"
                step="0.01"
                className="input"
                value={form.maxCostUsd}
                onChange={(e) => setForm({ ...form, maxCostUsd: e.target.value })}
              />
            </Field>
          </div>
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(1)} className="btn-secondary">
              Voltar
            </button>
            <button
              type="button"
              disabled={create.isPending}
              onClick={() => create.mutate()}
              className="btn-primary"
            >
              {create.isPending ? 'Planejando…' : 'Planejar campanha'}
            </button>
          </div>
          {create.error && <p className="text-red-600 text-sm">Erro: {create.error.message}</p>}
        </Panel>
      )}

      {step === 3 && plan && (
        <Panel>
          <h2 className="font-medium">Plano gerado</h2>
          <p className="text-sm text-slate-600">{plan.rationale}</p>
          {plan.llmModel && (
            <p className="text-xs text-slate-500">
              IA: {plan.llmProvider} · {plan.llmModel}
            </p>
          )}
          {plan.mapsConfigured === false && (
            <p role="status" className="text-sm text-amber-800 bg-amber-50 p-3 rounded-lg">
              O plano está pronto. Peça ao administrador para configurar o Google Maps e gere o
              plano novamente antes de iniciar.
            </p>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Keywords" value={plan.keywords.join(', ')} />
            <Info label="Cidades" value={plan.cities.join(', ')} />
            <Info label="Queries" value={plan.queries} />
            <Info
              label="Estimativa de busca (Maps)"
              value={usdFromMicros(plan.estimatedCostMicroUsd)}
            />
          </div>
          {['openai', 'gemini'].includes(plan.llmProvider) && (
            <p className="text-xs text-slate-500">
              A IA também cobra por tokens. O limite de consumo é verificado antes de cada chamada.
            </p>
          )}
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => setStep(2)} className="btn-secondary">
              Ajustar
            </button>
            <button
              type="button"
              disabled={startM.isPending || plan.mapsConfigured === false}
              onClick={() => startM.mutate()}
              className="btn-primary"
            >
              {startM.isPending ? 'Iniciando…' : 'Iniciar campanha'}
            </button>
          </div>
          {startM.error && <p className="text-red-600 text-sm">Erro: {startM.error.message}</p>}
        </Panel>
      )}
    </div>
  );
}

function Panel({ children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">{children}</div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-700 mb-1 font-medium">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-slate-500 text-xs uppercase tracking-wide">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function Stepper({ step }) {
  const steps = ['Intenção', 'Alvo', 'Confirmar'];
  return (
    <ol className="flex gap-2 text-xs">
      {steps.map((s, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <li
            key={s}
            className={`px-3 py-1 rounded ${active ? 'bg-brand-600 text-white' : done ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}
          >
            {n}. {s}
          </li>
        );
      })}
    </ol>
  );
}
